import * as _ from 'lodash-es';
import {AccountType} from '../../../enums/account-type';
import {Exchange} from '../../../enums/exchange';
import {OrderAlgoType} from '../../../enums/order-algo-type';
import {ExchangeAccountId} from '../../../models/exchange-account';
import {NewOrder} from '../../../models/order';
import {OrderAlgoParams} from '../../../models/order-algo-params';
import {Security} from '../../../models/security';
import {
    formatSecurityKey,
    isSecurityKeysEqual,
    parseSecurityKey,
    parseSecurityKeyArr,
    SecurityKey
} from '../../../models/security-key';
import {OrderFormValue} from '../../../widgets/components/float-widget-common/order-modal-form-block';
import {AccountService} from '../../accounts';
import {AspreaderSecurity, parseAsecurityConfig} from './aspreader-security';
import {AsecurityLegsAccountDict, AsecurityLegsAccountDictRec} from './aspreader-security-widget-settings';

export interface OrderAspreaderAlgoParamsAccountItem {
    securityKey: [string, Exchange, string];
    accountId: ExchangeAccountId;
    leverage: number;
    makerOnly: boolean;
    maxShow: number | null | undefined;
    maxUnfilled: number | null | undefined;
}

export interface OrderAspreaderAlgoParams extends OrderAlgoParams {
    algoType: OrderAlgoType.AUTOSPREADER;
    accountSecurities: OrderAspreaderAlgoParamsAccountItem[];
}

export const parseOrderAspreaderAlgoParamsAccounts = (
    paramsAccounts: OrderAspreaderAlgoParamsAccountItem[],
    accountService: AccountService
): AsecurityLegsAccountDict =>
    (paramsAccounts || []).reduce(
        (dict, item) => {
            const securityKey: SecurityKey = parseSecurityKeyArr(item.securityKey);
            const accountType: AccountType = item.leverage
                ? AccountType.MARGIN
                : AccountType.CASH;
            const accountBase = accountService.validateAccountBaseOrDefaultFromCache(
                {
                    id: item.accountId,
                    type: accountType
                }
            );

            dict[formatSecurityKey(securityKey)] = {
                accountBase,
                leverage:    item.leverage,
                makerOnly:   !!item.makerOnly,
                maxShow:     item.maxShow,
                maxUnfilled: item.maxUnfilled
            };

            return dict;
        },
        {} as AsecurityLegsAccountDict
    );

export const prepareOrderAspreaderAlgoParamsAccounts = (
    security: AspreaderSecurity | Security,
    legsAccountDict: AsecurityLegsAccountDict
): OrderAspreaderAlgoParamsAccountItem[] => {
    const asecurityConfig = parseAsecurityConfig(security);

    return Object.keys(legsAccountDict).reduce(
        (acc, key) => {
            const securityKey = parseSecurityKey(key);
            const rec: AsecurityLegsAccountDictRec = legsAccountDict[key];
            const leg = asecurityConfig.legs.find(itLeg => isSecurityKeysEqual(itLeg.securityKey, securityKey));

            if (leg) {
                acc.push({
                    securityKey: [securityKey.symbol, securityKey.securityExchange, securityKey.currency],
                    accountId:   rec.accountBase && rec.accountBase.id,
                    leverage:    rec.leverage,
                    makerOnly:   !!rec.makerOnly,
                    maxShow:     leg.activeQuoting && _.isFinite(rec.maxShow) ? rec.maxShow : undefined,
                    maxUnfilled: leg.activeQuoting && _.isFinite(rec.maxUnfilled) ? rec.maxUnfilled : undefined
                });
            }

            return acc;
        },
        [] as OrderAspreaderAlgoParamsAccountItem[]
    );
};

export const getOrderAspreaderAlgoParamsFormFields = (
    order: NewOrder,
    accountService: AccountService
): Partial<OrderFormValue> => {
    const algoParams = order.algoParams as OrderAspreaderAlgoParams;
    let result: Partial<OrderFormValue>;

    if (algoParams && algoParams.algoType === OrderAlgoType.AUTOSPREADER) {
        result = {
            legsAccountDict: parseOrderAspreaderAlgoParamsAccounts(
                algoParams.accountSecurities,
                accountService
            )
        };
    }

    return result;
};
